/**
 * SentEmails — Phase L
 * Unified AMC-level inbox of every email the platform has ever sent
 * (drawdown notices, reminders, acknowledgments, SOA emails, adhoc
 * Investor Communication). Pulls flat per-recipient rows from
 * /api/sent-emails so compliance officers can audit one row at a time.
 *
 * Filters: kind, template, status, date range, free-text search.
 * Engagement metrics: per-row open count + click count + first-opened-at.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Mail, Filter, Calendar, RefreshCw, ChevronLeft, ChevronRight,
  CheckCircle, XCircle, Clock, AlertCircle, Eye, MousePointer, Search,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending: { cls: 'bg-gray-100 text-gray-700',     icon: Clock,       label: 'Pending' },
  sent:    { cls: 'bg-green-100 text-green-700',   icon: CheckCircle, label: 'Sent' },
  failed:  { cls: 'bg-red-100 text-red-700',       icon: XCircle,     label: 'Failed' },
  skipped: { cls: 'bg-yellow-100 text-yellow-700', icon: AlertCircle, label: 'Skipped' },
  aborted: { cls: 'bg-amber-100 text-amber-700',   icon: AlertCircle, label: 'Aborted' },
};

const fmtDateTime = (s) => {
  if (!s) return '—';
  try {
    const d = new Date(s);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return s;
  }
};

const SummaryStat = ({ label, value, accent, testid }) => (
  <div
    className={`px-4 py-3 rounded-lg border ${accent} flex flex-col`}
    data-testid={testid}
  >
    <span className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">{label}</span>
    <span className="text-xl font-bold text-gray-900 mt-0.5">{value.toLocaleString()}</span>
  </div>
);

const SentEmails = () => {
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    kind: '', template_id: '', status: '', from_date: '', to_date: '', q: '',
  });
  const [filterMeta, setFilterMeta] = useState({ kinds: [], statuses: [], templates: [], scope: 'self' });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [data, setData] = useState({ rows: [], total: 0, summary: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Initial filter vocabulary
  useEffect(() => {
    (async () => {
      try {
        const { data: meta } = await axios.get(`${API}/sent-emails/filters`);
        setFilterMeta(meta);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Failed to load filters');
      }
    })();
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, page_size: pageSize };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data: payload } = await axios.get(`${API}/sent-emails`, { params });
      setData(payload);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((data.total || 0) / pageSize)),
    [data.total, pageSize]
  );

  const updateFilter = (key, value) => {
    setFilters(f => ({ ...f, [key]: value }));
    setPage(1);
  };

  const clearAll = () => {
    setFilters({ kind: '', template_id: '', status: '', from_date: '', to_date: '', q: '' });
    setPage(1);
  };

  const summary = data.summary || {};

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="sent-emails-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="text-blue-600" size={26} />
            Sent Emails
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Audit every email the platform has sent — drawdown notices, reminders, acknowledgments, SOA, and adhoc.
            {filterMeta.scope === 'self' && (
              <span className="ml-2 text-amber-700">Showing only your sends. Admins see all AMC sends.</span>
            )}
          </p>
        </div>
        <button
          onClick={fetchRows}
          className="px-3 py-2 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1.5 text-gray-700"
          data-testid="sent-emails-refresh"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="sent-emails-summary">
        <SummaryStat label="Total Recipients" value={data.total || 0} accent="bg-white border-gray-200"      testid="sent-emails-stat-total" />
        <SummaryStat label="Sent"             value={summary.sent || 0}    accent="bg-green-50 border-green-200" testid="sent-emails-stat-sent" />
        <SummaryStat label="Failed"           value={summary.failed || 0}  accent="bg-red-50 border-red-200"     testid="sent-emails-stat-failed" />
        <SummaryStat label="Skipped"          value={summary.skipped || 0} accent="bg-yellow-50 border-yellow-200" testid="sent-emails-stat-skipped" />
        <SummaryStat label="Opened"           value={summary.opened || 0}  accent="bg-blue-50 border-blue-200"   testid="sent-emails-stat-opened" />
        <SummaryStat label="Clicked"          value={summary.clicked || 0} accent="bg-indigo-50 border-indigo-200" testid="sent-emails-stat-clicked" />
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={15} className="text-gray-500" />
          <h2 className="text-sm font-semibold text-gray-700">Filters</h2>
          {(filters.kind || filters.template_id || filters.status || filters.from_date || filters.to_date || filters.q) && (
            <button
              type="button"
              onClick={clearAll}
              className="ml-auto text-xs text-blue-600 hover:underline"
              data-testid="sent-emails-clear-filters"
            >Clear all</button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {/* Kind */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Email Type</label>
            <select
              value={filters.kind}
              onChange={e => updateFilter('kind', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              data-testid="sent-emails-filter-kind"
            >
              <option value="">All types</option>
              {filterMeta.kinds.map(k => (
                <option key={k.value} value={k.value}>
                  {k.label} ({k.count})
                </option>
              ))}
            </select>
          </div>

          {/* Template */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Template</label>
            <select
              value={filters.template_id}
              onChange={e => updateFilter('template_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              data-testid="sent-emails-filter-template"
            >
              <option value="">All templates</option>
              {filterMeta.templates.map(t => (
                <option key={t.id} value={t.id}>{t.name} ({t.count})</option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Recipient Status</label>
            <select
              value={filters.status}
              onChange={e => updateFilter('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              data-testid="sent-emails-filter-status"
            >
              <option value="">All statuses</option>
              {filterMeta.statuses.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {/* From date */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
              <Calendar size={12} /> From
            </label>
            <input
              type="date"
              value={filters.from_date}
              onChange={e => updateFilter('from_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              data-testid="sent-emails-filter-from"
            />
          </div>

          {/* To date */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
              <Calendar size={12} /> To
            </label>
            <input
              type="date"
              value={filters.to_date}
              onChange={e => updateFilter('to_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              data-testid="sent-emails-filter-to"
            />
          </div>

          {/* Search */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
              <Search size={12} /> Search
            </label>
            <input
              type="text"
              value={filters.q}
              onChange={e => updateFilter('q', e.target.value)}
              placeholder="Investor name, email, template…"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              data-testid="sent-emails-filter-q"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {error && (
          <div className="px-4 py-3 bg-red-50 border-b border-red-200 text-sm text-red-700" data-testid="sent-emails-error">
            {error}
          </div>
        )}
        {loading ? (
          <div className="p-8 text-center text-sm text-gray-500" data-testid="sent-emails-loading">Loading…</div>
        ) : (data.rows || []).length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500" data-testid="sent-emails-empty">
            <Mail className="mx-auto mb-2 text-gray-300" size={36} />
            No sent emails match the current filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <Th>Sent At</Th>
                  <Th>Type</Th>
                  <Th>Recipient</Th>
                  <Th>Template</Th>
                  <Th>Sender</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Engagement</Th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {(data.rows || []).map((r, i) => {
                  const meta = STATUS_META[r.status] || STATUS_META.pending;
                  const Icon = meta.icon;
                  return (
                    <tr key={`${r.job_id}-${r.account_id}-${i}`} data-testid={`sent-emails-row-${i}`} className="hover:bg-gray-50">
                      <Td className="text-xs whitespace-nowrap text-gray-600">
                        {fmtDateTime(r.sent_at || r.job_created_at)}
                      </Td>
                      <Td>
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700">
                          {r.kind_label}
                        </span>
                      </Td>
                      <Td>
                        <div className="font-medium text-sm text-gray-900">{r.account_name || '—'}</div>
                        <div className="text-xs text-gray-500 truncate max-w-[260px]">
                          {(r.to_emails || []).join(', ') || '—'}
                        </div>
                      </Td>
                      <Td className="text-sm text-gray-700">{r.template_name || '—'}</Td>
                      <Td className="text-xs text-gray-500">
                        <div>{r.sender_name}</div>
                        <div className="truncate max-w-[180px]">{r.sender_email}</div>
                      </Td>
                      <Td>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${meta.cls}`}>
                          <Icon size={10} /> {meta.label}
                        </span>
                        {r.error && (
                          <div className="text-[10px] text-red-600 mt-1 max-w-[200px] truncate" title={r.error}>
                            {r.error}
                          </div>
                        )}
                      </Td>
                      <Td className="text-right">
                        <div className="flex items-center gap-3 justify-end">
                          <span
                            title={r.first_opened_at ? `First opened ${fmtDateTime(r.first_opened_at)}` : 'Not opened yet'}
                            className={`inline-flex items-center gap-1 text-xs ${(r.open_count || 0) > 0 ? 'text-blue-600 font-semibold' : 'text-gray-400'}`}
                            data-testid={`sent-emails-opens-${i}`}
                          >
                            <Eye size={13} /> {r.open_count || 0}
                          </span>
                          <span
                            title="Clicks"
                            className={`inline-flex items-center gap-1 text-xs ${(r.click_count || 0) > 0 ? 'text-indigo-600 font-semibold' : 'text-gray-400'}`}
                            data-testid={`sent-emails-clicks-${i}`}
                          >
                            <MousePointer size={13} /> {r.click_count || 0}
                          </span>
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {data.total > pageSize && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm bg-gray-50">
            <span className="text-gray-500" data-testid="sent-emails-pagination-info">
              Showing {Math.min((page - 1) * pageSize + 1, data.total)}–
              {Math.min(page * pageSize, data.total)} of {data.total}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-2 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed bg-white"
                data-testid="sent-emails-page-prev"
              >
                <ChevronLeft size={14} />
              </button>
              <span className="text-gray-600">Page {page} of {totalPages}</span>
              <button
                type="button"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-2 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed bg-white"
                data-testid="sent-emails-page-next"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Th = ({ children, className = '' }) => (
  <th className={`px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider ${className}`}>
    {children}
  </th>
);

const Td = ({ children, className = '' }) => (
  <td className={`px-3 py-2.5 align-top ${className}`}>{children}</td>
);

export default SentEmails;
